// node-key.js
// A stable network identity (libp2p peer ID) for this browser, derived from
// the identity key instead of generated fresh on every connect.
//
// Before this, each Connect created a brand-new random libp2p key, so every
// reload or reconnect produced a new peer ID and silently invalidated every
// address already shared. Deriving the key means the same unlocked identity
// always yields the same peer ID, with nothing extra stored: the identity
// private key is already encrypted at rest (passphrase / WebAuthn PRF), and
// this key only exists after that unlock.
//
// HKDF is one-way, so the peer ID does not reveal the identity public key or
// fingerprint to anyone who sees only the network address; the two are
// linkable only by whoever holds the identity key (and by group peers, who
// already learn the peer-to-identity mapping through MLS).
//
// One consequence: two browser tabs on the same origin share an identity, so
// they would now also share a peer ID and collide on the relay. They already
// could not both be used (shared identity breaks MLS), so this isn't new, but
// it's no longer masked by random IDs.

import { generateKeyPairFromSeed } from '@libp2p/crypto/keys'

const SALT = new TextEncoder().encode('p2p-core')
const INFO = new TextEncoder().encode('libp2p-node-key/v1')

/**
 * @param {{privateKey: {raw: Uint8Array}}} identity - an unlocked identity (see identity-core.js)
 * @returns {Promise<import('@libp2p/interface').PrivateKey>} a libp2p Ed25519 private key
 */
export async function deriveNodePrivateKey (identity) {
  const ikm = await globalThis.crypto.subtle.importKey('raw', identity.privateKey.raw, 'HKDF', false, ['deriveBits'])
  const bits = await globalThis.crypto.subtle.deriveBits({ name: 'HKDF', hash: 'SHA-256', salt: SALT, info: INFO }, ikm, 256)
  return generateKeyPairFromSeed('Ed25519', new Uint8Array(bits))
}
